/** @fileoverview Draws the bar, donut and line plots of a chart block in plain SVG and HTML, with a tooltip that follows the pointer. */

import { useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, PointerEvent as ReactPointerEvent, ReactNode, RefObject } from 'react';

import type { FabrikChartComponent, FabrikChartData } from '../../types/apps';
import { jsonPointerSegment } from '../../lib/previewSelection';
import {
  chartColorOf,
  drawnValue,
  formatCompact,
  formatExact,
  formatPercent,
  niceTicks,
} from './chartFormat';

type Measure = FabrikChartComponent['measure'];

/** What every plot gets: the numbers, the measure and the space it may use. */
export type PlotProps = {
  data: FabrikChartData;
  measure: Measure;
  width: number;
  height: number;
  selectionPath?: string;
};

/** One line of a tooltip: a color mark, a name and an exact value. */
type TooltipRow = { color?: string; label: string; value: string };

/** What the tooltip shows and where, in pixels inside the plot box. */
type TooltipState = { x: number; y: number; title: string; rows: TooltipRow[] } | null;

/* Space the axis text needs per character, a fair guess for the small app font. */
const CHAR_WIDTH = 6.5;
const AXIS_TEXT_CLASS = 'fill-muted-foreground type-caption';
/* Thickest a vertical bar may be; the rest of its slot stays empty. */
const MAX_BAR_WIDTH = 28;
/* Space between two touching bar parts. */
const GAP = 2;
/* Rounding of a bar's data end. */
const BAR_RADIUS = 4;

/**
 * Measures the width of an element and follows it when the layout changes.
 * Returns a ref to put on the element and its width in pixels, 0 before the first measure.
 */
export const useElementWidth = (): [RefObject<HTMLDivElement | null>, number] => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) {
      return undefined;
    }
    setWidth(element.clientWidth);
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
};

/**
 * Cuts a label to a number of characters, with an ellipsis when it is longer.
 * @param label the text to cut
 * @param maxChars the most characters to keep
 */
const shorten = (label: string, maxChars: number): string =>
  label.length <= maxChars ? label : `${label.slice(0, Math.max(1, maxChars - 1))}…`;

/**
 * Keeps track of the tooltip and of the pointer inside the plot box.
 * The pointer position is read from the box, so every plot places its tooltip the same way.
 */
const useTooltip = () => {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [tooltip, setTooltip] = useState<TooltipState>(null);

  /**
   * Shows the tooltip at the pointer.
   * @param event the pointer event over a mark
   * @param content the tooltip's title and rows
   */
  const showAt = (
    event: ReactPointerEvent<Element>,
    content: { title: string; rows: TooltipRow[] }
  ) => {
    const box = boxRef.current?.getBoundingClientRect();
    if (!box) {
      return;
    }
    setTooltip({ x: event.clientX - box.left, y: event.clientY - box.top, ...content });
  };

  return { boxRef, tooltip, showAt, hide: () => setTooltip(null) };
};

/**
 * Small box near the pointer with the exact values; it flips to the left near the right edge.
 * @param props the tooltip state and the width of the plot box
 */
const Tooltip = ({ tooltip, boxWidth }: { tooltip: TooltipState; boxWidth: number }) => {
  if (!tooltip) {
    return null;
  }
  const { x, y, title, rows } = tooltip;
  const flip = x > boxWidth / 2;
  return (
    <div
      className={`pointer-events-none absolute top-(--tip-top) z-10 min-w-36 max-w-64 rounded-md border border-border bg-popover px-2.5 py-2 type-caption text-popover-foreground shadow-md ${
        flip ? 'right-(--tip-side)' : 'left-(--tip-side)'
      }`}
      style={
        {
          '--tip-top': `${y + 12}px`,
          '--tip-side': `${flip ? Math.max(0, boxWidth - x + 12) : x + 12}px`,
        } as CSSProperties
      }
    >
      <p className="mb-1 font-semibold wrap-anywhere">{title}</p>
      <ul className="space-y-0.5">
        {rows.map(({ color, label, value }, rowIndex) => (
          <li key={`${label}-${rowIndex}`} className="flex items-center gap-2">
            {color && (
              <span
                className="size-2 shrink-0 rounded-xs bg-(--chart-color)"
                style={{ '--chart-color': color } as CSSProperties}
                aria-hidden="true"
              />
            )}
            <span className="min-w-0 flex-1 truncate text-muted-foreground">{label}</span>
            <span className="font-medium tabular-nums">{value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * Frame of a plot: a box the tooltip is placed in, which hides the tooltip when the pointer leaves.
 * @param props the tooltip parts and the plot
 */
const PlotBox = ({
  boxRef,
  tooltip,
  width,
  onLeave,
  children,
}: {
  boxRef: RefObject<HTMLDivElement | null>;
  tooltip: TooltipState;
  width: number;
  onLeave: () => void;
  children: ReactNode;
}) => (
  <div ref={boxRef} className="relative" onPointerLeave={onLeave}>
    {children}
    <Tooltip tooltip={tooltip} boxWidth={width} />
  </div>
);

/**
 * Tooltip rows of one category: each series' value and, for a split count or sum, the total.
 * @param data the chart's numbers
 * @param measure the chart's measure
 * @param categoryIndex the category position
 */
const categoryRows = (data: FabrikChartData, measure: Measure, categoryIndex: number) => {
  const { series, values } = data;
  const rows: TooltipRow[] = series.map(({ key, label }, seriesIndex) => ({
    color: chartColorOf(key, seriesIndex),
    label,
    value: formatExact(values[seriesIndex]?.[categoryIndex] ?? null, measure),
  }));
  if (series.length > 1 && measure?.fn !== 'average') {
    const total = series.reduce(
      (sum, _series, seriesIndex) => sum + drawnValue(data, seriesIndex, categoryIndex),
      0
    );
    rows.push({ label: 'Total', value: formatExact(total, measure) });
  }
  return rows;
};

/**
 * Lowest and highest end of the stacked bars, so the axis covers every stack.
 * @param data the chart's numbers
 */
const stackRange = (data: FabrikChartData) => {
  const { categories, series } = data;
  let low = 0;
  let high = 0;
  categories.forEach((_category, categoryIndex) => {
    let positive = 0;
    let negative = 0;
    series.forEach((_series, seriesIndex) => {
      const value = drawnValue(data, seriesIndex, categoryIndex);
      if (value >= 0) {
        positive += value;
      } else {
        negative += value;
      }
    });
    high = Math.max(high, positive);
    low = Math.min(low, negative);
  });
  return { low, high };
};

/**
 * One stacked part of a bar: where it starts and ends on the value scale, and whether it is the
 * outer end of its stack, which gets the rounded corners.
 */
type BarPart = { seriesIndex: number; start: number; end: number; isOuter: boolean };

/**
 * Splits one category into stacked parts: positive values go up from 0, negative values down.
 * @param data the chart's numbers
 * @param categoryIndex the category position
 */
const barPartsOf = (data: FabrikChartData, categoryIndex: number): BarPart[] => {
  let positive = 0;
  let negative = 0;
  const parts = data.series.flatMap((_series, seriesIndex): BarPart[] => {
    const value = drawnValue(data, seriesIndex, categoryIndex);
    if (value === 0) {
      return [];
    }
    const start = value > 0 ? positive : negative;
    if (value > 0) {
      positive += value;
    } else {
      negative += value;
    }
    return [{ seriesIndex, start, end: start + value, isOuter: false }];
  });
  const lastUp = parts.filter(({ end }) => end > 0).pop();
  const lastDown = parts.filter(({ end }) => end < 0).pop();
  return parts.map((part) => ({ ...part, isOuter: part === lastUp || part === lastDown }));
};

/**
 * SVG path of a vertical bar part with rounded corners on its outer end only.
 * @param box the part's left, top, width and height in pixels, and which end is rounded
 */
const columnPath = ({
  x,
  y,
  width,
  height,
  rounded,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  rounded: 'top' | 'bottom' | 'none';
}): string => {
  const radius = rounded === 'none' ? 0 : Math.min(BAR_RADIUS, height, width / 2);
  const right = x + width;
  const bottom = y + height;
  if (rounded === 'top') {
    return `M${x},${bottom}V${y + radius}Q${x},${y} ${x + radius},${y}H${
      right - radius
    }Q${right},${y} ${right},${y + radius}V${bottom}Z`;
  }
  if (rounded === 'bottom') {
    return `M${x},${y}V${bottom - radius}Q${x},${bottom} ${x + radius},${bottom}H${
      right - radius
    }Q${right},${bottom} ${right},${bottom - radius}V${y}Z`;
  }
  return `M${x},${y}H${right}V${bottom}H${x}Z`;
};

/**
 * Value axis of the column and line plots: round steps, their short labels, and hairline grid.
 * @param props the steps, the scale, the left edge and the plot width
 */
const ValueAxis = ({
  ticks,
  scale,
  left,
  plotWidth,
}: {
  ticks: number[];
  scale: (value: number) => number;
  left: number;
  plotWidth: number;
}) => (
  <g aria-hidden="true">
    {ticks.map((tick) => (
      <g key={tick}>
        <line
          x1={left}
          x2={left + plotWidth}
          y1={scale(tick)}
          y2={scale(tick)}
          className={tick === 0 ? 'stroke-border' : 'stroke-border/60'}
          strokeWidth={1}
        />
        <text
          x={left - 6}
          y={scale(tick)}
          dy="0.32em"
          textAnchor="end"
          className={`${AXIS_TEXT_CLASS} tabular-nums`}
        >
          {formatCompact(tick)}
        </text>
      </g>
    ))}
  </g>
);

/**
 * Sizes shared by the column and line plots: the margins, the value steps and the value scale.
 * @param props the plot box size and the lowest and highest value to show
 */
const axisFrame = ({
  width,
  height,
  low,
  high,
  right = 8,
}: {
  width: number;
  height: number;
  low: number;
  high: number;
  /** Space kept right of the plot, such as for the last date label. */
  right?: number;
}) => {
  const ticks = niceTicks(low, high);
  const tickChars = Math.max(...ticks.map((tick) => formatCompact(tick).length));
  const left = tickChars * CHAR_WIDTH + 10;
  const top = 8;
  const bottom = 24;
  const plotWidth = Math.max(1, width - left - right);
  const plotHeight = Math.max(1, height - top - bottom);
  const first = ticks[0];
  const last = ticks[ticks.length - 1];
  const scale = (value: number) => top + plotHeight * (1 - (value - first) / (last - first));
  return { ticks, left, top, plotWidth, plotHeight, scale };
};

/**
 * Vertical bars, stacked when the chart is split, with a value axis and the category names below.
 * @param props the numbers, the measure and the space to use
 */
export const ColumnPlot = ({ data, measure, width, height, selectionPath }: PlotProps) => {
  const { boxRef, tooltip, showAt, hide } = useTooltip();
  const [hovered, setHovered] = useState<number | null>(null);
  const { categories } = data;
  const { low, high } = stackRange(data);
  const { ticks, left, top, plotWidth, plotHeight, scale } = axisFrame({
    width,
    height,
    low,
    high,
  });
  const band = plotWidth / categories.length;
  const barWidth = Math.min(MAX_BAR_WIDTH, band * 0.7);
  const labelChars = Math.max(3, Math.floor((band - 4) / CHAR_WIDTH));

  return (
    <PlotBox
      boxRef={boxRef}
      tooltip={tooltip}
      width={width}
      onLeave={() => {
        hide();
        setHovered(null);
      }}
    >
      <svg width={width} height={height} aria-hidden="true" className="block overflow-visible">
        <ValueAxis ticks={ticks} scale={scale} left={left} plotWidth={plotWidth} />
        {categories.map(({ key, label }, categoryIndex) => {
          const categoryPath = selectionPath
            ? `${selectionPath}/runtime/categories/${categoryIndex}`
            : undefined;
          const bandLeft = left + band * categoryIndex;
          const barLeft = bandLeft + (band - barWidth) / 2;
          return (
            <g
              key={key}
              data-fabrik-selection-path={categoryPath}
              data-fabrik-selection-definition-path={
                selectionPath ? `${selectionPath}/groupBy` : undefined
              }
            >
              {hovered === categoryIndex && (
                <rect
                  x={bandLeft}
                  y={top}
                  width={band}
                  height={plotHeight}
                  className="fill-muted"
                />
              )}
              {barPartsOf(data, categoryIndex).map(({ seriesIndex, start, end, isOuter }) => {
                const isUp = end > 0;
                /* The part touching 0 sits on the baseline; others leave a small gap below. */
                const gap = start === 0 ? 0 : GAP;
                const near = scale(start) + (isUp ? -gap : gap);
                const far = scale(end);
                const y = Math.min(near, far);
                const partHeight = Math.max(0, Math.abs(near - far));
                return (
                  <path
                    key={data.series[seriesIndex].key}
                    data-fabrik-selection-path={
                      categoryPath ? `${categoryPath}/series/${seriesIndex}` : undefined
                    }
                    data-fabrik-selection-definition-path={
                      selectionPath ? `${selectionPath}/measure` : undefined
                    }
                    d={columnPath({
                      x: barLeft,
                      y,
                      width: barWidth,
                      height: partHeight,
                      rounded: isOuter ? (isUp ? 'top' : 'bottom') : 'none',
                    })}
                    className="fill-(--chart-color)"
                    style={
                      {
                        '--chart-color': chartColorOf(data.series[seriesIndex].key, seriesIndex),
                      } as CSSProperties
                    }
                  />
                );
              })}
              <text
                data-fabrik-selection-path={categoryPath ? `${categoryPath}/label` : undefined}
                data-fabrik-selection-definition-path={
                  selectionPath ? `${selectionPath}/groupBy` : undefined
                }
                x={bandLeft + band / 2}
                y={top + plotHeight + 16}
                textAnchor="middle"
                className={AXIS_TEXT_CLASS}
              >
                {shorten(label, labelChars)}
              </text>
              <rect
                x={bandLeft}
                y={top}
                width={band}
                height={plotHeight + 24}
                fill="transparent"
                onPointerEnter={() => setHovered(categoryIndex)}
                onPointerMove={(event) =>
                  showAt(event, { title: label, rows: categoryRows(data, measure, categoryIndex) })
                }
              />
            </g>
          );
        })}
      </svg>
    </PlotBox>
  );
};

/**
 * Horizontal bars, one row per category with its name on the left and the total at the bar's
 * end, used when there are many categories or long names.
 * @param props the numbers and the measure; the rows set their own height
 */
export const RowPlot = ({ data, measure, width, selectionPath }: PlotProps) => {
  const { boxRef, tooltip, showAt, hide } = useTooltip();
  const { categories, series } = data;
  const { low, high } = stackRange(data);
  const span = high - low || 1;
  /**
   * Position on the bar track as a percent of its width.
   * @param value the value to place
   */
  const percentOf = (value: number) => ((value - low) / span) * 100;

  return (
    <PlotBox boxRef={boxRef} tooltip={tooltip} width={width} onLeave={hide}>
      <ul className="space-y-1" aria-hidden="true">
        {categories.map(({ key, label }, categoryIndex) => {
          const categoryPath = selectionPath
            ? `${selectionPath}/runtime/categories/${categoryIndex}`
            : undefined;
          const total = series.reduce(
            (sum, _series, seriesIndex) => sum + drawnValue(data, seriesIndex, categoryIndex),
            0
          );
          const shown =
            series.length === 1 || measure?.fn !== 'average'
              ? formatExact(
                  series.length === 1 ? data.values[0]?.[categoryIndex] ?? null : total,
                  measure
                )
              : '';
          return (
            <li
              key={key}
              data-fabrik-selection-path={categoryPath}
              data-fabrik-selection-definition-path={
                selectionPath ? `${selectionPath}/groupBy` : undefined
              }
              className="flex items-center gap-3 rounded-sm px-1 py-0.5 hover:bg-muted"
              onPointerMove={(event) =>
                showAt(event, { title: label, rows: categoryRows(data, measure, categoryIndex) })
              }
            >
              <span
                data-fabrik-selection-path={categoryPath ? `${categoryPath}/label` : undefined}
                data-fabrik-selection-definition-path={
                  selectionPath ? `${selectionPath}/groupBy` : undefined
                }
                className="w-36 shrink-0 truncate type-caption text-muted-foreground"
                title={label}
              >
                {label}
              </span>
              <span className="relative h-4 min-w-0 flex-1">
                {barPartsOf(data, categoryIndex).map(({ seriesIndex, start, end, isOuter }) => {
                  const isRight = end > 0;
                  const gap = start === 0 ? 0 : GAP;
                  const from = percentOf(Math.min(start, end));
                  const length = percentOf(Math.max(start, end)) - from;
                  return (
                    <span
                      key={series[seriesIndex].key}
                      data-fabrik-selection-path={
                        categoryPath ? `${categoryPath}/series/${seriesIndex}` : undefined
                      }
                      data-fabrik-selection-definition-path={
                        selectionPath ? `${selectionPath}/measure` : undefined
                      }
                      className={`absolute inset-y-0 left-(--part-left) w-(--part-width) bg-(--chart-color) ${
                        isOuter ? (isRight ? 'rounded-r-sm' : 'rounded-l-sm') : ''
                      }`}
                      style={
                        {
                          /* The part touching 0 sits on the baseline; others leave a small gap. */
                          '--part-left': isRight ? `calc(${from}% + ${gap}px)` : `${from}%`,
                          '--part-width': `max(0px, calc(${length}% - ${gap}px))`,
                          '--chart-color': chartColorOf(series[seriesIndex].key, seriesIndex),
                        } as CSSProperties
                      }
                    />
                  );
                })}
              </span>
              <span
                data-fabrik-selection-path={categoryPath ? `${categoryPath}/value` : undefined}
                data-fabrik-selection-definition-path={
                  selectionPath ? `${selectionPath}/measure` : undefined
                }
                className="shrink-0 type-caption text-card-foreground tabular-nums"
              >
                {shown}
              </span>
            </li>
          );
        })}
      </ul>
    </PlotBox>
  );
};

/**
 * SVG path of one donut slice between two angles, in radians from the top, clockwise.
 * @param slice the center, the outer and inner radius, and the start and end angle
 */
const slicePath = ({
  center,
  outer,
  inner,
  start,
  end,
}: {
  center: number;
  outer: number;
  inner: number;
  start: number;
  end: number;
}): string => {
  /* A whole circle cannot be one arc, so it is drawn as two halves. */
  if (end - start >= Math.PI * 2 - 1e-6) {
    return `${slicePath({ center, outer, inner, start, end: start + Math.PI })}${slicePath({
      center,
      outer,
      inner,
      start: start + Math.PI,
      end,
    })}`;
  }
  /**
   * Point on a circle around the center.
   * @param radius the circle's radius
   * @param angle the angle from the top, clockwise
   */
  const point = (radius: number, angle: number) =>
    `${center + radius * Math.sin(angle)},${center - radius * Math.cos(angle)}`;
  const large = end - start > Math.PI ? 1 : 0;
  return `M${point(outer, start)}A${outer},${outer} 0 ${large} 1 ${point(outer, end)}L${point(
    inner,
    end
  )}A${inner},${inner} 0 ${large} 0 ${point(inner, start)}Z`;
};

/**
 * A donut with the total in the middle and a legend of each slice's value and share.
 * An average chart shows no total or shares, since averages do not add up.
 * @param props the numbers, the measure and the space to use
 */
export const DonutPlot = ({ data, measure, width, height, selectionPath }: PlotProps) => {
  const { boxRef, tooltip, showAt, hide } = useTooltip();
  const [hovered, setHovered] = useState<number | null>(null);
  const { categories } = data;
  const isAverage = measure?.fn === 'average';
  const sliceValues = categories.map((_category, categoryIndex) =>
    Math.max(0, drawnValue(data, 0, categoryIndex))
  );
  const total = sliceValues.reduce((sum, value) => sum + value, 0);
  const isNarrow = width < 420;
  const size = Math.max(120, Math.min(height, isNarrow ? width : width * 0.45));
  const center = size / 2;
  const outer = center - 2;
  const inner = outer * 0.62;
  let angle = 0;
  const slices = sliceValues.map((value, categoryIndex) => {
    const start = angle;
    angle += total > 0 ? (value / total) * Math.PI * 2 : 0;
    return { categoryIndex, start, end: angle, value };
  });
  /**
   * Tooltip rows of one slice: its value and, for counts and sums, its share.
   * @param categoryIndex the slice's category position
   */
  const sliceRows = (categoryIndex: number): TooltipRow[] => [
    {
      color: chartColorOf(categories[categoryIndex].key, categoryIndex),
      label: data.series[0]?.label ?? '',
      value: formatExact(data.values[0]?.[categoryIndex] ?? null, measure),
    },
    ...(isAverage || total === 0
      ? []
      : [{ label: 'Share', value: formatPercent(sliceValues[categoryIndex] / total) }]),
  ];

  return (
    <PlotBox
      boxRef={boxRef}
      tooltip={tooltip}
      width={width}
      onLeave={() => {
        hide();
        setHovered(null);
      }}
    >
      <div className={`flex items-center gap-6 ${isNarrow ? 'flex-col' : 'flex-row'}`}>
        <div
          className="relative size-(--donut-size) shrink-0"
          style={{ '--donut-size': `${size}px` } as CSSProperties}
        >
          <svg width={size} height={size} aria-hidden="true" className="block">
            {total === 0 ? (
              <path
                d={slicePath({ center, outer, inner, start: 0, end: Math.PI * 2 })}
                className="fill-(--fabrik-chart-other)"
              />
            ) : (
              slices
                .filter(({ value }) => value > 0)
                .map(({ categoryIndex, start, end }) => (
                  <path
                    key={categories[categoryIndex].key}
                    data-fabrik-selection-path={
                      selectionPath
                        ? `${selectionPath}/runtime/categories/${categoryIndex}/slice`
                        : undefined
                    }
                    data-fabrik-selection-definition-path={
                      selectionPath ? `${selectionPath}/groupBy` : undefined
                    }
                    d={slicePath({
                      center,
                      outer: hovered === categoryIndex ? outer + 2 : outer,
                      inner,
                      start,
                      end,
                    })}
                    className="fill-(--chart-color) stroke-card"
                    /* A slice that is the whole circle has no neighbour, so it needs no gap. */
                    strokeWidth={end - start >= Math.PI * 2 - 1e-6 ? 0 : GAP}
                    style={
                      {
                        '--chart-color': chartColorOf(categories[categoryIndex].key, categoryIndex),
                      } as CSSProperties
                    }
                    onPointerEnter={() => setHovered(categoryIndex)}
                    onPointerMove={(event) =>
                      showAt(event, {
                        title: categories[categoryIndex].label,
                        rows: sliceRows(categoryIndex),
                      })
                    }
                  />
                ))
            )}
          </svg>
          {!isAverage && (
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span
                data-fabrik-selection-path={
                  selectionPath ? `${selectionPath}/runtime/total` : undefined
                }
                data-fabrik-selection-definition-path={
                  selectionPath ? `${selectionPath}/measure` : undefined
                }
                className="type-heading font-semibold tabular-nums"
                title={formatExact(total, measure)}
              >
                {formatCompact(total)}
              </span>
              <span className="type-caption text-muted-foreground">Total</span>
            </div>
          )}
        </div>
        <ul className="w-full min-w-0 flex-1 space-y-1.5" aria-hidden="true">
          {categories.map(({ key, label }, categoryIndex) => {
            const categoryPath = selectionPath
              ? `${selectionPath}/runtime/categories/${categoryIndex}`
              : undefined;
            return (
              <li
                key={key}
                data-fabrik-selection-path={categoryPath}
                data-fabrik-selection-definition-path={
                  selectionPath ? `${selectionPath}/groupBy` : undefined
                }
                className={`flex items-center gap-2 rounded-sm px-1 type-small ${
                  hovered === categoryIndex ? 'bg-muted' : ''
                }`}
                onPointerEnter={() => setHovered(categoryIndex)}
                onPointerLeave={() => setHovered(null)}
              >
                <span
                  className="size-2.5 shrink-0 rounded-xs bg-(--chart-color)"
                  style={{ '--chart-color': chartColorOf(key, categoryIndex) } as CSSProperties}
                />
                <span
                  data-fabrik-selection-path={categoryPath ? `${categoryPath}/label` : undefined}
                  className="min-w-0 flex-1 truncate text-card-foreground"
                  title={label}
                >
                  {label}
                </span>
                <span
                  data-fabrik-selection-path={categoryPath ? `${categoryPath}/value` : undefined}
                  data-fabrik-selection-definition-path={
                    selectionPath ? `${selectionPath}/measure` : undefined
                  }
                  className="font-medium tabular-nums text-card-foreground"
                >
                  {formatExact(data.values[0]?.[categoryIndex] ?? null, measure)}
                </span>
                {!isAverage && total > 0 && (
                  <span
                    data-fabrik-selection-path={categoryPath ? `${categoryPath}/share` : undefined}
                    className="w-12 text-right tabular-nums text-muted-foreground"
                  >
                    {formatPercent(sliceValues[categoryIndex] / total)}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </PlotBox>
  );
};

/**
 * Shortens a day or week step for the axis: "Week of 11 May 2026" and "11 May 2026" become
 * "11 May". Months, quarters and years stay as they are; the tooltip keeps the full text.
 * @param label the step label the server sent
 */
const axisLabelOf = (label: string) =>
  label.replace(/^Week of /, '').replace(/^(\d{1,2} [A-Z][a-z]{2}) \d{4}$/, '$1');

/**
 * One line per series over the date steps in order, with a dot on each value and a guide at the
 * step under the pointer. An average with no value leaves a gap in its line.
 * @param props the numbers, the measure and the space to use
 */
export const LinePlot = ({ data, measure, width, height, selectionPath }: PlotProps) => {
  const { boxRef, tooltip, showAt, hide } = useTooltip();
  const [hovered, setHovered] = useState<number | null>(null);
  const { categories, series, values } = data;
  const known = values.flat().filter((value): value is number => value !== null);
  const axisLabels = categories.map(({ label }) => axisLabelOf(label));
  const longest = Math.max(...axisLabels.map((label) => label.length));
  const { ticks, left, top, plotWidth, plotHeight, scale } = axisFrame({
    width,
    height,
    low: Math.min(0, ...known),
    high: Math.max(0, ...known),
    /* Half of the last date label hangs past the last point. */
    right: ((axisLabels[axisLabels.length - 1]?.length ?? 0) * CHAR_WIDTH) / 2 + 4,
  });
  const step = categories.length > 1 ? plotWidth / (categories.length - 1) : 0;
  /**
   * Left position of one date step.
   * @param categoryIndex the step position
   */
  const xOf = (categoryIndex: number) =>
    categories.length > 1 ? left + step * categoryIndex : left + plotWidth / 2;
  /* Every nth label shows, so the labels do not run into each other. */
  const labelEvery = Math.max(1, Math.ceil((longest * CHAR_WIDTH + 12) / Math.max(step, 1)));

  /**
   * Picks the date step nearest the pointer and shows its values.
   * @param event the pointer event over the plot
   */
  const pointAt = (event: ReactPointerEvent<SVGRectElement>) => {
    const svgLeft = event.currentTarget.ownerSVGElement?.getBoundingClientRect().left ?? 0;
    const offset = event.clientX - svgLeft - left;
    const categoryIndex =
      categories.length > 1
        ? Math.min(categories.length - 1, Math.max(0, Math.round(offset / step)))
        : 0;
    setHovered(categoryIndex);
    showAt(event, {
      title: categories[categoryIndex].label,
      rows: categoryRows(data, measure, categoryIndex),
    });
  };

  return (
    <PlotBox
      boxRef={boxRef}
      tooltip={tooltip}
      width={width}
      onLeave={() => {
        hide();
        setHovered(null);
      }}
    >
      <svg width={width} height={height} aria-hidden="true" className="block overflow-visible">
        <ValueAxis ticks={ticks} scale={scale} left={left} plotWidth={plotWidth} />
        {categories.map(({ key }, categoryIndex) =>
          categoryIndex % labelEvery === 0 ? (
            <text
              key={key}
              data-fabrik-selection-path={
                selectionPath
                  ? `${selectionPath}/runtime/categories/${categoryIndex}/label`
                  : undefined
              }
              data-fabrik-selection-definition-path={
                selectionPath ? `${selectionPath}/groupBy` : undefined
              }
              x={xOf(categoryIndex)}
              y={top + plotHeight + 16}
              textAnchor="middle"
              className={AXIS_TEXT_CLASS}
            >
              {axisLabels[categoryIndex]}
            </text>
          ) : null
        )}
        {hovered !== null && (
          <line
            x1={xOf(hovered)}
            x2={xOf(hovered)}
            y1={top}
            y2={top + plotHeight}
            className="stroke-muted-foreground/50"
            strokeWidth={1}
          />
        )}
        {series.map(({ key }, seriesIndex) => {
          const color = chartColorOf(key, seriesIndex);
          /* A value of null ends one stretch of the line; the next value starts a new one. */
          const path = categories
            .map((_category, categoryIndex) => {
              const value = values[seriesIndex]?.[categoryIndex] ?? null;
              if (value === null) {
                return '';
              }
              const previous =
                categoryIndex > 0 ? values[seriesIndex]?.[categoryIndex - 1] ?? null : null;
              return `${previous === null ? 'M' : 'L'}${xOf(categoryIndex)},${scale(value)}`;
            })
            .join('');
          return (
            <g
              key={key}
              data-fabrik-selection-path={
                selectionPath
                  ? `${selectionPath}/runtime/series/${jsonPointerSegment(key)}`
                  : undefined
              }
              data-fabrik-selection-definition-path={
                selectionPath ? `${selectionPath}/splitBy` : undefined
              }
              style={{ '--chart-color': color } as CSSProperties}
            >
              <path
                d={path}
                fill="none"
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
                className="stroke-(--chart-color)"
              />
              {categories.map(({ key: categoryKey }, categoryIndex) => {
                const value = values[seriesIndex]?.[categoryIndex] ?? null;
                return value === null ? null : (
                  <circle
                    key={categoryKey}
                    data-fabrik-selection-path={
                      selectionPath
                        ? `${selectionPath}/runtime/series/${jsonPointerSegment(
                            key
                          )}/categories/${categoryIndex}`
                        : undefined
                    }
                    data-fabrik-selection-definition-path={
                      selectionPath ? `${selectionPath}/measure` : undefined
                    }
                    cx={xOf(categoryIndex)}
                    cy={scale(value)}
                    r={hovered === categoryIndex ? 5 : 4}
                    className="fill-(--chart-color) stroke-card"
                    strokeWidth={2}
                  />
                );
              })}
            </g>
          );
        })}
        <rect
          data-fabrik-selection-path={selectionPath ? `${selectionPath}/runtime/plot` : undefined}
          data-fabrik-selection-definition-path={
            selectionPath ? `${selectionPath}/groupBy` : undefined
          }
          x={left - Math.max(step / 2, 8)}
          y={top}
          width={plotWidth + Math.max(step, 16)}
          height={plotHeight}
          fill="transparent"
          onPointerMove={pointAt}
        />
      </svg>
    </PlotBox>
  );
};
